import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from 'react-router-dom';
import { pushEvent } from '../../utils/analytics';
import { fetchCatalog } from '../../api/checkout';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import TrustBar from '../../components/TrustBar/TrustBar';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import {
  buildBookedCallCalendarLinks,
  fetchBootcampOfferSummary,
  formatBootcampOfferDate,
} from './challengePageUtils';
import './ChallengePageClarity.css';
import './ChallengeBookingPage.css';

const BOOTCAMP_PATH = '/courses/ai-trading-bootcamp';

// Rob, 2026-09-11: this page lives at /challenges/call-confirmed with no run in
// the URL. The booking comes from navigation state, then session storage.
function readCallContext(state) {
  if (state?.callContext) return state.callContext;
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('challengeCallContext') || '{}');
    return saved && typeof saved === 'object' ? saved : {};
  } catch {
    return {};
  }
}

// Real photos of the people who take these calls. Anyone else gets an initial.
const HOST_PHOTOS = {
  robert: '/images/challenges/hosts/rob.jpg',
  rob: '/images/challenges/hosts/rob.jpg',
};

// Shown when the booking cannot be read back, so the page never loses its face.
const DEFAULT_HOST = 'Robert Grzesik';

function hostPhoto(name) {
  const first = String(name || '')
    .trim()
    .split(/\s+/)[0]
    .toLowerCase();
  return HOST_PHOTOS[first] || null;
}

// The registrant's first name, saved by the registration step on this device.
function readFirstName() {
  try {
    const saved = JSON.parse(window.sessionStorage.getItem('challengeBookingContext') || '{}');
    const first = String(saved?.name || '')
      .trim()
      .split(/\s+/)[0];
    return first && first.length <= 40 ? first : null;
  } catch {
    return null;
  }
}

const APPOINTMENT_TIME_FORMAT = {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZoneName: 'short',
};

/**
 * Format the appointment in the visitor's own time zone: the one they chose in
 * Calendly when the server knows it, otherwise the browser's.
 */
function appointmentTime(value, timezone) {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) return null;
  try {
    return date.toLocaleString('en-US', { ...APPOINTMENT_TIME_FORMAT, timeZone: timezone });
  } catch {
    return date.toLocaleString('en-US', APPOINTMENT_TIME_FORMAT);
  }
}

export default function ChallengeCallConfirmedPage() {
  const location = useLocation();
  const callContext = useMemo(() => readCallContext(location.state), [location.state]);
  const [booking, setBooking] = useState({
    status: callContext.readToken ? 'loading' : 'unverified',
  });
  const [bootcampOffer, setBootcampOffer] = useState(null);
  const [tuitionWindow, setTuitionWindow] = useState(null);
  const [offerLoaded, setOfferLoaded] = useState(false);

  useEffect(() => {
    if (!callContext.readToken) {
      setBooking({ status: 'unverified' });
      return undefined;
    }
    let active = true;
    let timer;
    const abort = new AbortController();
    const deadline = window.setTimeout(() => {
      abort.abort();
      if (active) setBooking({ status: 'unverified' });
    }, 20000);
    setBooking({ status: 'loading' });
    const readBooking = () =>
      fetch(`${getApiBaseUrl() || 'https://api.botspot.trade'}/calendly/booking`, {
        headers: { Authorization: `Bearer ${callContext.readToken}` },
        signal: abort.signal,
        cache: 'no-store',
      });
    let inviteReads = 0;
    // Only ever adds the invite link to a booking already shown; a failed
    // re-read leaves the verified booking on screen as it is.
    const verifyInvite = async () => {
      try {
        const response = await readBooking();
        if (!response.ok) return;
        const result = await response.json();
        if (!active || result.status !== 'scheduled') return;
        if (result.googleCalendarUrl) {
          setBooking(result);
        } else if (inviteReads < 4) {
          inviteReads += 1;
          timer = window.setTimeout(() => verifyInvite(), 2000);
        }
      } catch {
        // Keep the booking that is already verified on screen.
      }
    };
    const verify = async (attempt = 0) => {
      try {
        const response = await readBooking();
        if (!response.ok) throw new Error('verification_unavailable');
        const result = await response.json();
        if (!active) return;
        if (result.status === 'pending' && attempt < 7) {
          timer = window.setTimeout(() => verify(attempt + 1), 1500);
          return;
        }
        window.clearTimeout(deadline);
        setBooking(
          ['scheduled', 'canceled', 'pending'].includes(result.status)
            ? result
            : { status: 'unverified' },
        );
        // Calendly writes the Google event a moment after the booking, so the
        // first readback can lack the guest's invite link (Rob, 2026-10-20).
        // Show the booking now and read it a few more times for the link.
        if (result.status === 'scheduled' && !result.googleCalendarUrl && inviteReads < 4) {
          inviteReads += 1;
          timer = window.setTimeout(() => verifyInvite(), 2000);
        }
      } catch {
        window.clearTimeout(deadline);
        if (active) setBooking({ status: 'unverified' });
      }
    };
    verify();
    return () => {
      active = false;
      abort.abort();
      window.clearTimeout(timer);
      window.clearTimeout(deadline);
    };
  }, [callContext.readToken]);

  useEffect(() => {
    pushEvent({
      event: 'call_booking_confirmation_viewed',
      source: window.location.pathname,
      referrer: document.referrer || 'calendly_embed',
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadOffer = async () => {
      try {
        const summary = await fetchBootcampOfferSummary(fetchCatalog);
        if (!mounted) return;
        setBootcampOffer({
          name: summary.name,
          cohortName: summary.cohortName,
          startsAt: summary.startsAt,
        });
        setTuitionWindow(summary.tuitionWindow);
      } catch {
        if (!mounted) return;
        setBootcampOffer(null);
        setTuitionWindow(null);
      } finally {
        if (mounted) setOfferLoaded(true);
      }
    };
    loadOffer();
    return () => {
      mounted = false;
    };
  }, []);

  const confirmedTime = useMemo(
    () =>
      booking.status === 'scheduled' ? appointmentTime(booking.startsAt, booking.timezone) : null,
    [booking.status, booking.startsAt, booking.timezone],
  );

  const firstName = useMemo(() => readFirstName(), []);
  const verified = booking.status === 'scheduled' && Boolean(confirmedTime);
  const canceled = booking.status === 'canceled';
  // Rob, 2026-10-16: one page for everyone who booked. When the booking cannot
  // be read back, keep the same design with Rob as the host and hide only the
  // pieces that need the booking data (exact time, calendar and Zoom links).
  const host = verified ? (booking.hosts || [])[0] || null : DEFAULT_HOST;
  const calendarLinks = useMemo(
    () => (verified ? buildBookedCallCalendarLinks(booking, getApiBaseUrl()) : null),
    [verified, booking],
  );
  const zoomUrl = verified && /^https:\/\//.test(booking.joinUrl || '') ? booking.joinUrl : null;
  const rescheduleUrl =
    verified && /^https:\/\/calendly\.com\//.test(booking.rescheduleUrl || '')
      ? booking.rescheduleUrl
      : null;
  const googleInviteUrl =
    verified && /^https:\/\/calendar\.google\.com\//.test(booking.googleCalendarUrl || '')
      ? booking.googleCalendarUrl
      : null;
  const track = (action) => () =>
    pushEvent({ event: 'call_show_up_action', action, source: window.location.pathname });

  const nextCohortDate = useMemo(
    () => formatBootcampOfferDate(bootcampOffer?.startsAt),
    [bootcampOffer?.startsAt],
  );
  const enrollmentEnds = useMemo(
    () => formatBootcampOfferDate(tuitionWindow?.endDate),
    [tuitionWindow?.endDate],
  );

  // Record which state the visitor saw once the readback settles, so the share
  // of bookers who never get the verified details is measurable.
  const settledState =
    booking.status === 'loading'
      ? null
      : verified
        ? 'verified'
        : canceled
          ? 'canceled'
          : 'unverified';
  useEffect(() => {
    if (!settledState) return;
    pushEvent({
      event: 'call_booking_confirmation_state',
      state: settledState,
      source: window.location.pathname,
    });
  }, [settledState]);

  // Rob, 2026-10-01 review: the booking is laid out like the best appointment
  // confirmations (Zocdoc upcoming visit, Lyssna session page): the person you
  // will meet is the focal point, the time and the calendar actions sit with
  // them, and what to prepare fills the second column on desktop.
  const hostImage = host ? hostPhoto(host) : null;

  return (
    <main
      className="challenge-next challenge-next--confirmed challenge-clarity-review"
      data-testid="challenge-call-confirmed-page"
      data-education-design-variant="clarity"
      data-education-theme="dark"
      data-experiment-active="false"
    >
      <Helmet>
        <title>Your call details | BotSpot</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="challenge-next__brand">
        <img src={botspotLogo} alt="BotSpot by Lumiwealth" width="1384" height="430" />
      </div>
      <>
        {canceled ? (
          <header aria-live="polite" className="challenge-next__confirmed-head">
            <h1 className="challenge-next__confirmation-line">This call was canceled.</h1>
            <p className="challenge-next__confirmation-sub">
              <Link to="/challenges/welcome">Pick a new time</Link> whenever it suits you.
            </p>
          </header>
        ) : (
          <header aria-live="polite" className="challenge-next__confirmed-head">
            <p className="challenge-next__booked-badge">✓ You&apos;re booked</p>
            <h1 className="challenge-next__confirmation-line">
              {firstName ? `You're all set, ${firstName}.` : "You're all set."}
            </h1>
            <p className="challenge-next__confirmation-sub">
              Two quick things make sure nothing slips. They take about a minute.
            </p>
          </header>
        )}
        <div className="challenge-next__confirmed-grid">
          <section
            className="challenge-next__call-card"
            data-testid="call-confirmed-card"
            aria-label="Appointment details"
          >
            {host ? (
              <div className="challenge-next__call-host">
                {hostImage ? (
                  <img src={hostImage} alt={host} width="400" height="400" />
                ) : (
                  <span className="challenge-next__call-host-initial" aria-hidden="true">
                    {host.trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <p>
                  <strong>{host}</strong>
                  <span>will be on the call with you</span>
                </p>
              </div>
            ) : null}
            {verified ? (
              <div className="challenge-next__call-time">
                <p className="challenge-next__call-when">{confirmedTime}</p>
                <p className="challenge-next__call-meta">
                  {calendarLinks?.minutes || 30}-minute Zoom call · your time ({booking.timezone})
                </p>
              </div>
            ) : canceled ? null : (
              <div className="challenge-next__call-time">
                <p className="challenge-next__call-when">
                  Your exact time is in your confirmation email.
                </p>
                <p className="challenge-next__call-meta">30-minute Zoom call</p>
              </div>
            )}
            <div className="challenge-next__call-actions">
              {googleInviteUrl ? (
                <a
                  className="challenge-next__primary"
                  href={googleInviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={track('accept_google_invite')}
                >
                  Accept in Google Calendar
                </a>
              ) : calendarLinks ? (
                <a
                  className="challenge-next__primary"
                  href={calendarLinks.google}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={track('add_google_calendar')}
                >
                  Add to Google Calendar
                </a>
              ) : null}
              {calendarLinks ? (
                <a
                  className="challenge-next__secondary"
                  href={calendarLinks.ics}
                  onClick={track('add_ics_calendar')}
                >
                  Add to Apple or Outlook calendar
                </a>
              ) : null}
              {zoomUrl ? (
                <a
                  className="challenge-next__secondary"
                  href={zoomUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={track('open_zoom_link')}
                >
                  Open Zoom Link
                </a>
              ) : null}
            </div>
          </section>
          {canceled ? null : (
            <section className="challenge-next__show-up" data-testid="call-confirmed-prep">
              <h2>Two quick things</h2>
              <ol className="challenge-next__show-up-steps">
                <li>
                  <strong>Put it in your calendar.</strong>{' '}
                  {calendarLinks
                    ? 'Tap the calendar button, or open the invite we emailed you and click Yes.'
                    : 'Open the invite we emailed you and click Yes.'}{' '}
                  {/* Rob, 2026-10-20: a reply keeps our reminders out of spam. */}
                  If our email is in spam or Promotions, move it to your inbox and reply
                  &quot;in&quot;.
                </li>
                <li>
                  <strong>Save the Zoom link.</strong> The call happens on Zoom at the time shown.
                </li>
              </ol>
              <h2>What happens on the call</h2>
              <p>
                {host || 'We'} will look at how you trade today, show you how AI can research and
                trade for you, and tell you honestly whether the AI Trading Bootcamp is a fit.
              </p>
              <h2>Have these ready</h2>
              <ul className="challenge-next__show-up-ready">
                <li>A laptop and a quiet spot, with your camera on</li>
                <li>Your broker account, logged in</li>
                <li>Roughly how much you trade or invest with</li>
                <li>One trade or idea you want help with</li>
                <li>If someone else is part of this decision, have them join you</li>
              </ul>
              {rescheduleUrl ? (
                <p className="challenge-next__reschedule">
                  Something came up?{' '}
                  <a href={rescheduleUrl} onClick={track('reschedule')}>
                    Reschedule instead of missing it
                  </a>
                  .
                </p>
              ) : null}
            </section>
          )}
        </div>
      </>
      <div className="challenge-next__confirmation-layout challenge-next__confirmation-layout--offer-only">
        <section className="challenge-next__course" aria-labelledby="challenge-offer-title">
          <p className="challenge-next__course-bridge">
            Before we talk, see what the five weeks cover.
          </p>
          <div className="challenge-next__course-heading">
            <img
              src="/images/challenges/guided-research.png"
              alt="A decision-tree notebook and magnifying glass for guided research"
              width="1024"
              height="1024"
            />
            <div>
              <p className="challenge-next__course-label">The live AI Trading Bootcamp</p>
              <h2 id="challenge-offer-title">Build your own AI trading workflow.</h2>
            </div>
          </div>
          <p className="challenge-next__course-benefit">
            Go from scattered tools to a research process you understand, with live instructor
            guidance.
          </p>
          <Link
            className="challenge-next__primary"
            to={`${BOOTCAMP_PATH}?utm_source=challenge_call_confirmed&utm_medium=booking&utm_campaign=ai_trading_challenge`}
          >
            Explore the Bootcamp <span aria-hidden="true">→</span>
          </Link>
          <p className="challenge-next__course-hint">See the curriculum, dates &amp; tuition</p>
          {offerLoaded ? (
            <>
              <div className="challenge-next__offer-facts" aria-label="Current Bootcamp details">
                {nextCohortDate ? <span>Next cohort: {nextCohortDate}</span> : null}
                {enrollmentEnds ? (
                  <span>
                    {tuitionWindow.label || 'Enrollment'} ends {enrollmentEnds}
                  </span>
                ) : null}
              </div>
            </>
          ) : (
            <p className="challenge-next__status">Loading current dates…</p>
          )}
        </section>
      </div>
      <TrustBar />
    </main>
  );
}
